import { useEffect, useRef, useState } from "react";

// Phase 5 live-first briefing: live quotes alongside the edition tape.
// - WebSocket first; HTTP poll every 15s only after the socket closes, with
//   reconnect backoff capped at 30s. Polling never runs while the socket is up.
// - Payloads are validated: a malformed snapshot is ignored, never rendered.
// - Live quotes are shown ONLY on the current desk page, never on a dated
//   archive edition (an archive must show what was published then).
// - Times are rendered in America/Chicago (Austin); the backend stores UTC.
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/research/editions`;

export function formatChicagoTime(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    timeZone: "America/Chicago", timeZoneName: "short",
  }).format(date);
}

function validSnapshot(data) {
  if (!data || !Array.isArray(data.quotes)) return null;
  const quotes = data.quotes.filter(q =>
    q && typeof q.symbol === "string" && Number.isFinite(Number(q.price)));
  if (!quotes.length) return null;
  if (!["live", "poll", "unavailable"].includes(data.mode)) return null;
  return { mode: data.mode, asof: data.asof || null, quotes };
}

export function useLiveQuotes(enabled) {
  const [live, setLive] = useState(null);
  const state = useRef({ socket: null, poll: null, reconnect: null, delay: 1000, disposed: false });

  useEffect(() => {
    const s = state.current;
    s.disposed = false;
    if (!enabled || typeof WebSocket === "undefined") return () => { s.disposed = true; };

    const wsUrl = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/api/research/editions/live`;

    const stopPoll = () => { if (s.poll) { clearInterval(s.poll); s.poll = null; } };
    const pollOnce = async () => {
      try {
        const response = await fetch(`${API}/live/quotes`, { cache: "no-store" });
        if (!response.ok) return;
        const snap = validSnapshot(await response.json());
        if (snap && !s.disposed) setLive(snap);
      } catch { /* retain last live quotes; edition tape remains the fallback */ }
    };
    const startPoll = () => {
      if (!s.poll) { pollOnce(); s.poll = setInterval(pollOnce, 15000); }
    };
    const connect = () => {
      if (s.disposed) return;
      stopPoll();
      let socket;
      try { socket = new WebSocket(wsUrl); } catch { startPoll(); return; }
      s.socket = socket;
      socket.onmessage = event => {
        try {
          const snap = validSnapshot(JSON.parse(event.data));
          if (snap && !s.disposed) setLive(snap);
        } catch { /* ignore malformed frames */ }
      };
      socket.onclose = () => {
        if (s.disposed) return;
        startPoll();
        s.delay = Math.min(s.delay * 2, 30000);
        s.reconnect = setTimeout(connect, s.delay);
      };
      socket.onerror = () => { try { socket.close(); } catch {} };
    };

    connect();
    return () => {
      s.disposed = true;
      stopPoll();
      if (s.reconnect) clearTimeout(s.reconnect);
      try { s.socket && s.socket.close(); } catch {}
    };
  }, [enabled]);

  return live;
}
